package com.evoapps.evoshift.domain.market.payroll.poland

import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.MonthWorkInput
import com.evoapps.evoshift.domain.market.payroll.WorkedSpan
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.RatePeriod
import com.evoapps.evoshift.domain.model.SystemContractType
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.math.max
import kotlin.math.min
import kotlin.time.Duration.Companion.minutes

/**
 * SH-57: what a month adds to an employment contract's pay in Poland, as an estimate.
 *
 * - **Overtime** (Kodeks pracy art. 151, 151¹): work over the daily norm of 8 hours (art. 129 § 1),
 *   counted per calendar day of the shift's start, earns 50%, or 100% for the hours at night and on
 *   Sundays and public holidays (the roster cannot tell whether a Sunday was the person's working
 *   day, so the higher supplement is assumed). In the settlement period's last month the hours over
 *   the period's norm (average 40 hours a week: [PolishCalendar.workingHours] of its months, times
 *   the FTE) that are not daily overtime earn 100% (art. 151¹ § 2).
 * - **Sunday and holiday work** (art. 151¹¹): another day off, or, when none is left before the end of
 *   the period, the 100% supplement for each hour. The estimate takes "no day off left" to mean the
 *   person worked over the period's norm: of the hours over the norm, those on Sundays and holidays
 *   get this supplement first, the rest are overtime over the average weekly norm.
 * - **Time off in lieu** (art. 151²): with [WorkplacePayrollSettings.overtimeTimeOffInLieu] the hours
 *   above are given back as time off and no supplement is paid.
 * - **Minimum wage** (ustawa o minimalnym wynagrodzeniu za pracę): an employee's month may not be
 *   under the minimum wage for the hours they are employed for (pro rata FTE); overtime pay and the
 *   night allowance do not count towards it. The difference is paid as a top-up.
 * - **Civil-law contracts** (zlecenie) have no overtime: only the minimum hourly rate is checked, as
 *   a warning.
 *
 * - **Leave and pay-term changes** (SH-57): see [PolishLeavePay]; leave shortens the norms here.
 *
 * Part-time: overtime starts over the person's own norm (the FTE share), the strictest reading of
 * art. 151 § 5 when the contract sets no other limit. Hourly pay is treated as full time unless the
 * person's monthly hours are stated.
 */
internal object PolishWorkingTime {
    private const val DAILY_NORM_MINUTES = 8 * 60L

    /**
     * The month's factors: the pay terms in force on each day (SH-57), leave pay, and the
     * working-time factors with the norms shortened by the leave.
     */
    fun monthFactors(input: MonthWorkInput): MonthPayFactors {
        val rates = PolandRates.forYear(input.year)
        val contract = if (input.contract == SystemContractType.NOT_SET) SystemContractType.STANDARD_EMPLOYMENT else input.contract
        val first = PolishLeavePay.monthStart(input.year, input.month)
        val last = PolishLeavePay.monthEnd(input.year, input.month)
        val parts = PolishLeavePay.parts(input.ratePeriods, input.compensation, first, last)
        val monthSpans = input.shifts.filter { it.start.date in first..last && it.end > it.start }
        val terms = if (input.ratePeriods.isEmpty()) input.compensation else PolishLeavePay.blended(parts, monthSpans, input.year, input.month)
        val adjustment = if (input.ratePeriods.isEmpty()) 0.0
        else PolishLeavePay.rateChangeAdjustment(parts, monthSpans, input.year, input.month, input.compensation)
        val blendedInput = input.copy(compensation = terms)
        val fte = fte(blendedInput, PolishCalendar.workingHours(input.year, input.month).toDouble())
        val (leave, effect) = PolishLeavePay.leaveFactors(input, contract, parts, fte, rates)
        // SH-223: approved work outside the shifts counts as worked time (overtime and its supplements).
        val extra = input.extraWork.filter { it.start.date in first..last && it.end > it.start }
        val work = workFactors(if (extra.isEmpty()) blendedInput else blendedInput.copy(shifts = input.shifts + extra), effect)
        val extraHours = extra.sumOf { (it.end.toInstant(TimeZone.UTC) - it.start.toInstant(TimeZone.UTC)).inWholeMinutes } / 60.0
        val extraPay = extra.sumOf { span ->
            val terms = PolishLeavePay.compensationOn(input.ratePeriods, input.compensation, span.start.date) as? CompensationModel.Hourly
            (terms?.rate ?: 0.0) * (span.end.toInstant(TimeZone.UTC) - span.start.toInstant(TimeZone.UTC)).inWholeMinutes / 60.0
        }
        return work.copy(
            paidLeaveDays = leave.paidLeaveDays,
            paidLeaveHours = leave.paidLeaveHours,
            halfPaidLeaveDays = leave.halfPaidLeaveDays,
            halfPaidLeaveHours = leave.halfPaidLeaveHours,
            paidLeavePay = leave.paidLeavePay,
            unpaidLeaveDays = leave.unpaidLeaveDays,
            benefitLeaveDays = leave.benefitLeaveDays,
            unpaidLeaveReduction = leave.unpaidLeaveReduction,
            extraWorkHours = extraHours,
            extraWorkPay = PolandPayroll.money(extraPay),
            sickDays = leave.sickDays,
            sickPayDays = leave.sickPayDays,
            sickPay = leave.sickPay,
            sickBenefitDays = leave.sickBenefitDays,
            sickBenefit = leave.sickBenefit,
            sickPayBase = leave.sickPayBase,
            salaryReduction = leave.salaryReduction,
            vacationDays = leave.vacationDays,
            vacationHours = leave.vacationHours,
            holidayPay = leave.holidayPay,
            holidayVariablePay = leave.holidayVariablePay,
            leaveEntitlementDays = leave.leaveEntitlementDays,
            leaveUsedDays = leave.leaveUsedDays,
            rateChanges = if (parts.size > 1) parts.map { RatePeriod(it.from.toString(), it.compensation) } else emptyList(),
            rateChangeAdjustment = PolandPayroll.money(adjustment)
        )
    }

    /** The person's share of full time: a salary's FTE, or hourly pay's stated hours over the norm (else full time). */
    fun fte(input: MonthWorkInput, monthNorm: Double): Double = when (val compensation = input.compensation) {
        is CompensationModel.MonthlyFixed -> compensation.fte.takeIf { it > 0 } ?: 1.0
        is CompensationModel.Hourly -> input.declaredHours?.takeIf { it > 0 && monthNorm > 0 }?.let { min(1.0, it / monthNorm) } ?: 1.0
    }

    /** Overtime, Sunday and holiday work and the minimum wage, with the norms shortened by [leave]. */
    fun workFactors(input: MonthWorkInput, leave: PolishLeavePay.LeaveEffect): MonthPayFactors {
        val rates = PolandRates.forYear(input.year)
        val period = input.settings.settlementMonths(input.month)
        val firstMonth = period.first
        val lastMonth = period.last
        val index = input.year * 12 + input.month - 1
        val none = MonthPayFactors(input.year * 12 + firstMonth - 1, input.year * 12 + lastMonth - 1, 0.0, 0.0)

        val contract = if (input.contract == SystemContractType.NOT_SET) SystemContractType.STANDARD_EMPLOYMENT else input.contract
        if (contract == SystemContractType.CIVIL_LAW_CONTRACT) {
            val rate = (input.compensation as? CompensationModel.Hourly)?.rate
            return none.copy(minimumHourlyRate = rates.minimumHourlyRate.takeIf { rate != null && rate > 0 && rate < rates.minimumHourlyRate })
        }
        if (contract != SystemContractType.STANDARD_EMPLOYMENT) return none

        val monthNorm = PolishCalendar.workingHours(input.year, input.month).toDouble()
        val compensation = input.compensation
        val fte = fte(input, monthNorm)
        // Art. 130 § 3: each leave day on a working day shortens the norm by its hours.
        val leaveHours = rates.leaveDayHours * fte
        val periodNorm = max(0.0, (firstMonth..lastMonth).sumOf { PolishCalendar.workingHours(input.year, it) } * fte -
            leave.periodLeaveDays * leaveHours - leave.periodLeaveHours)

        // The period's work up to the end of this month, by the day it started.
        val periodFirstDay = LocalDate(input.year, firstMonth, 1)
        val spans = input.shifts.filter { it.end > it.start && it.start.date >= periodFirstDay && it.start.year == input.year && it.start.month.ordinal + 1 <= input.month }
        val inMonth = { span: WorkedSpan -> span.start.month.ordinal + 1 == input.month }

        // Daily overtime: per day, whatever is worked past the first 8 hours.
        var daily50 = 0L
        var daily100 = 0L
        var dailyPeriod = 0L
        var dailyOnSundayHoliday = 0L
        spans.groupBy { it.start.date }.forEach { (_, day) ->
            var before = 0L
            day.sortedBy { it.start }.forEach { span ->
                val length = minutesBetween(span.start, span.end)
                val within = max(0L, DAILY_NORM_MINUTES - before)
                before += length
                if (length <= within) return@forEach
                val overtimeStart = span.start.plusMinutes(within)
                pieces(overtimeStart, span.end).forEach { (from, to) ->
                    val minutes = minutesBetween(from, to)
                    val high = if (isSundayOrHoliday(from.date)) minutes.also { dailyOnSundayHoliday += it }
                    else PolandPayroll.nightMinutes(from, to, input.settings)
                    dailyPeriod += minutes
                    if (inMonth(span)) {
                        daily100 += high
                        daily50 += minutes - high
                    }
                }
            }
        }

        // The period's excess over its norm, settled in its last month.
        val settled = input.month == lastMonth
        val periodMinutes = spans.sumOf { minutesBetween(it.start, it.end) }
        val overNorm = max(0.0, periodMinutes / 60.0 - periodNorm)
        var sundayOver = 0.0
        var weeklyOver = 0.0
        if (settled) {
            val excess = max(0.0, overNorm - dailyPeriod / 60.0)
            val sundayMinutes = spans.sumOf { span -> pieces(span.start, span.end).filter { isSundayOrHoliday(it.first.date) }.sumOf { minutesBetween(it.first, it.second) } }
            sundayOver = min(excess, max(0L, sundayMinutes - dailyOnSundayHoliday) / 60.0)
            weeklyOver = excess - sundayOver
        }

        val base = when (compensation) {
            is CompensationModel.Hourly -> compensation.rate
            is CompensationModel.MonthlyFixed -> compensation.baseSalary / (monthNorm * fte)
        }
        val hours50 = daily50 / 60.0
        val hours100 = daily100 / 60.0 + weeklyOver
        val timeOff = input.settings.overtimeTimeOffInLieu
        val supplement50 = if (timeOff) 0.0 else hours50 * base * rates.overtimeSupplement
        val supplement100 = if (timeOff) 0.0 else hours100 * base * rates.overtimeSupplementHigh
        val sundaySupplement = if (timeOff) 0.0 else sundayOver * base * rates.overtimeSupplementHigh
        // Hourly pay already pays every hour; a salary covers the norm, the hours over it are paid on top.
        val overtimePay = if (!timeOff && settled && compensation is CompensationModel.MonthlyFixed) overNorm * base else 0.0

        // Minimum wage for the hours the person is employed for; overtime and night pay do not count.
        // The hours over the norms (overtime, Sundays and holidays without a day off) count as overtime.
        val monthHours = input.assumedHours?.takeIf { spans.isEmpty() }
            ?: (spans.filter(inMonth).sumOf { minutesBetween(it.start, it.end) } / 60.0)
        val normalHours = max(0.0, monthHours - hours50 - hours100 - sundayOver)
        // SH-57: sick days are not owed as work; annual leave is paid and counts.
        // SH-223: nor are unpaid hours; time off paid in full counts as paid hours.
        val sickHours = leave.monthSickWorkdays * leaveHours + leave.monthUnpaidHours
        val hoursToWork = max(0.0, when (compensation) {
            is CompensationModel.MonthlyFixed -> monthNorm * fte - sickHours
            is CompensationModel.Hourly -> input.declaredHours?.takeIf { it > 0 }?.let { min(it.toDouble(), monthNorm) - sickHours }
                ?: min(normalHours + leave.monthVacationHours + leave.monthPaidLeaveHours, monthNorm)
        })
        // Art. 7–8: the minimum wage pro rata to the hours to be worked (for hourly pay: per hour).
        val minimumPay = if (monthNorm > 0) rates.minimumWage * hoursToWork / monthNorm else 0.0
        val counted = when (compensation) {
            is CompensationModel.MonthlyFixed -> compensation.baseSalary - leave.salaryReduction - leave.unpaidReduction
            is CompensationModel.Hourly -> compensation.rate * normalHours + leave.holidayPay + leave.paidLeavePay
        } + input.otherCountedPay
        val topUp = max(0.0, minimumPay - counted)

        return none.copy(
            periodNormHours = periodNorm,
            periodWorkedHours = periodMinutes / 60.0,
            settled = settled,
            overtime50Hours = hours50,
            overtime100Hours = hours100,
            sundayHolidayHours = sundayOver,
            supplementBase = PolandPayroll.money(base),
            overtimePay = PolandPayroll.money(overtimePay),
            overtime50Supplement = PolandPayroll.money(supplement50),
            overtime100Supplement = PolandPayroll.money(supplement100),
            sundayHolidaySupplement = PolandPayroll.money(sundaySupplement),
            timeOffInLieuHours = if (timeOff) hours50 + hours100 + sundayOver else 0.0,
            minimumPay = PolandPayroll.money(minimumPay),
            minimumWageTopUp = PolandPayroll.money(topUp)
        ).also { check(index in it.periodStart..it.periodEnd) }
    }

    private fun isSundayOrHoliday(date: LocalDate) = date.dayOfWeek == DayOfWeek.SUNDAY || PolishCalendar.isHoliday(date)

    /** [from, to) cut at midnights. */
    private fun pieces(from: LocalDateTime, to: LocalDateTime): List<Pair<LocalDateTime, LocalDateTime>> {
        val out = mutableListOf<Pair<LocalDateTime, LocalDateTime>>()
        var start = from
        while (start < to) {
            val midnight = LocalDateTime(start.date.plus(DatePeriod(days = 1)), kotlinx.datetime.LocalTime(0, 0))
            val end = minOf(to, midnight)
            out += start to end
            start = end
        }
        return out
    }

    private fun LocalDateTime.plusMinutes(minutes: Long): LocalDateTime =
        (toInstant(TimeZone.UTC) + minutes.minutes).toLocalDateTime(TimeZone.UTC)

    private fun minutesBetween(from: LocalDateTime, to: LocalDateTime): Long =
        (to.toInstant(TimeZone.UTC) - from.toInstant(TimeZone.UTC)).inWholeMinutes
}
